import React, { useCallback, useContext, useMemo, useState } from "react";
import moment from "moment";
import { ClockCircleOutlined, TeamOutlined, UserSwitchOutlined } from "@ant-design/icons";
import lang from "translations";
import { getArrivalTimeStats } from "apis/booking.api";
import {
  Container,
  FilterBar,
  FilterDateRange,
  Icon,
  Image,
  Panel,
  Pill,
  Table,
  Text,
  Title,
} from "components/commons";
import { NoData } from "images/index";
import { HeaderA } from "components/headers";
import { ModuleWrapper } from "components/fragments";
import { VenueContext } from "contexts";
import { useApi, useMount, transactionDateStore } from "hooks";
import { PillType } from "enums";
import { formatNumberWithComma } from "services";
import { mixpanel, TrackEvent } from "mixpanel";
import ArrivalTimeChart, { GuestView } from "./arrival-time-chart";
import GuestTypeFilter from "./guest-type-filter";

const API_DATE_FORMAT = "YYYY-MM-DD";
const SLOT_TIME_FORMAT = "HH:mm";
const SLOT_LABEL_FORMAT = "HH:mm";

// the chart shows every arrival time slot of the day, empty ones keep their label
const CHART_SLOT_START = "11:00";
const CHART_SLOT_END = "21:00";
const CHART_SLOT_STEP_MINUTES = 20;

const buildChartSlots = (slots) => {
  const slotByTime = new Map(slots.map((slot) => [slot.time, slot]));
  const slotTimes = slots.map(({ time }) => moment(time, SLOT_TIME_FORMAT));
  const start = moment.min([moment(CHART_SLOT_START, SLOT_TIME_FORMAT), ...slotTimes]);
  const end = moment.max([moment(CHART_SLOT_END, SLOT_TIME_FORMAT), ...slotTimes]);

  const times = [];
  for (
    const time = start.clone();
    time.isSameOrBefore(end);
    time.add(CHART_SLOT_STEP_MINUTES, "minutes")
  ) {
    times.push(time.format(SLOT_TIME_FORMAT));
  }
  // arrival times that do not sit on the grid still deserve a column
  slots.forEach(({ time }) => {
    if (!times.includes(time)) {
      times.push(time);
    }
  });
  times.sort();

  return times.map((time) => {
    return (
      slotByTime.get(time) || {
        time,
        label: moment(time, SLOT_TIME_FORMAT).format(SLOT_LABEL_FORMAT),
        registeredGuests: 0,
        totalGuests: 0,
      }
    );
  });
};

const ArrivalTimeReport = () => {
  const { venue } = useContext(VenueContext);
  const { startDate } = transactionDateStore(venue);

  const defaultDateRange = useMemo(() => {
    const day = moment(startDate);
    return [day.clone().startOf("day"), day.clone().endOf("day")];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [dateRange, setDateRange] = useState(defaultDateRange);
  // empty selection means both guest types are shown
  const [guestTypes, setGuestTypes] = useState([]);
  const [sort, setSort] = useState({ key: "totalGuests", value: "desc" });

  const guestTypeOptions = useMemo(() => {
    return [
      { value: GuestView.Registered, text: lang.totalPreCheckinGuests },
      { value: GuestView.Total, text: lang.totalReservedGuests },
    ];
  }, []);

  const guestView = useMemo(() => {
    const selected = guestTypes.map(({ value }) => value);
    if (selected.length !== 1) {
      return GuestView.Both;
    }
    return selected[0];
  }, [guestTypes]);

  const {
    request: arrivalTimeStatsRequest,
    loading: loadingArrivalTimeStats,
    result: arrivalTimeStatsResult,
  } = useApi({
    api: getArrivalTimeStats,
  });

  const fetchArrivalTimeStats = useCallback(
    (range) => {
      arrivalTimeStatsRequest({ date: range[0].format(API_DATE_FORMAT) });
    },
    [arrivalTimeStatsRequest]
  );

  useMount(() => {
    fetchArrivalTimeStats(dateRange);

    mixpanel.track(TrackEvent.VisitedPage, {
      Page: lang.arrivalTimeReport,
    });
  });

  const changeDateCb = useCallback(
    (value) => {
      if (!value?.[0]) {
        return;
      }
      setDateRange(value);
      fetchArrivalTimeStats(value);
    },
    [fetchArrivalTimeStats]
  );

  const applyFilterCb = useCallback(() => {
    fetchArrivalTimeStats(dateRange);
  }, [dateRange, fetchArrivalTimeStats]);

  const clearFilterCb = useCallback(() => {
    setDateRange(defaultDateRange);
    fetchArrivalTimeStats(defaultDateRange);
  }, [defaultDateRange, fetchArrivalTimeStats]);

  const slots = useMemo(() => {
    const rawSlots = arrivalTimeStatsResult?.data?.slots || [];
    // always derive the label from `time` on 24h format, ignore whatever the API sends
    return rawSlots.map((slot) => ({
      ...slot,
      label: moment(slot.time, SLOT_TIME_FORMAT).format(SLOT_LABEL_FORMAT),
    }));
  }, [arrivalTimeStatsResult]);

  const chartSlots = useMemo(() => {
    if (!slots.length) {
      return [];
    }
    return buildChartSlots(slots);
  }, [slots]);

  const summaryCards = useMemo(() => {
    const reservedGuests = slots.reduce((total, slot) => total + (slot.totalGuests || 0), 0);
    const preCheckinGuests = slots.reduce(
      (total, slot) => total + (slot.registeredGuests || 0),
      0
    );
    return [
      {
        key: "reserved",
        Icon: TeamOutlined,
        label: lang.totalReservedGuests,
        value: reservedGuests,
      },
      {
        key: "preCheckin",
        Icon: UserSwitchOutlined,
        label: lang.totalPreCheckinGuests,
        value: preCheckinGuests,
      },
      {
        key: "waiting",
        Icon: ClockCircleOutlined,
        label: lang.waitForPreCheckinGuests,
        value: Math.max(reservedGuests - preCheckinGuests, 0),
      },
    ];
  }, [slots]);

  const peakTime = useMemo(() => {
    if (!slots.length) {
      return null;
    }
    return slots.reduce((peak, slot) => {
      return slot.totalGuests > peak.totalGuests ? slot : peak;
    }).time;
  }, [slots]);

  const columns = useMemo(() => {
    return [
      {
        key: "arrivalTime",
        text: lang.arrivalTime,
        sort: true,
      },
      {
        key: "registeredGuests",
        text: lang.totalPreCheckinGuests,
        sort: true,
      },
      {
        key: "totalGuests",
        text: lang.totalReservedGuests,
        sort: true,
      },
    ];
  }, []);

  const data = useMemo(() => {
    const sorted = [...slots].sort((a, b) => {
      const direction = sort.value === "asc" ? 1 : -1;
      if (sort.key === "arrivalTime") {
        return a.time > b.time ? direction : a.time < b.time ? -direction : 0;
      }
      return ((a[sort.key] || 0) - (b[sort.key] || 0)) * direction;
    });

    return sorted.map((slot) => {
      return {
        arrivalTime: (
          <div className="flex items-center">
            <Text className="mr-md whitespace-nowrap">{slot.label}</Text>
            {slot.time === peakTime && (
              <Pill type={PillType.Greenish} className="ml-sm">
                <div className="flex items-center" style={{ gap: 8 }}>
                  <Text size="text-xs">{lang.peakHour}</Text>
                  <Icon name="star" paddingless fontSize={10} />
                </div>
              </Pill>
            )}
          </div>
        ),
        registeredGuests: <Text>{slot.registeredGuests || 0}</Text>,
        totalGuests: <Text>{slot.totalGuests || 0}</Text>,
      };
    });
  }, [slots, sort, peakTime]);

  const hasData = slots.length > 0;

  return (
    <ModuleWrapper>
      <HeaderA
        title={lang.arrivalTimeStatisticView}
        description={lang.arrivalTimeStatisticViewDesc}
        className="mb-md"
      />

      <FilterBar
        className="my-lg"
        searchBox={false}
        showClear={false}
        loading={loadingArrivalTimeStats}
        filterState={{ dateRange }}
        onApply={applyFilterCb}
        onClear={clearFilterCb}
      >
        <FilterDateRange
          id="date"
          label={lang.date}
          singleDate
          showPresets={false}
          value={dateRange}
          onChange={changeDateCb}
        />
      </FilterBar>

      {hasData || loadingArrivalTimeStats ? (
        <>
          <div className="flex flex-wrap mb-md" style={{ gap: 16 }}>
            {summaryCards.map(({ key, Icon: CardIcon, label, value }) => (
              <div key={key} className="flex-none" style={{ minWidth: 280 }}>
                <Panel mb="">
                  <div className="flex items-center" style={{ gap: 16 }}>
                    <div
                      className="flex items-center justify-center rounded-full bg-pelorous-lighter text-pelorous"
                      style={{ width: 48, height: 48, flexShrink: 0 }}
                    >
                      <CardIcon style={{ fontSize: 20 }} />
                    </div>
                    <div>
                      <Text color="text-gray" size="text-sm">
                        {label}
                      </Text>
                      <Title lg>{formatNumberWithComma(value)}</Title>
                    </div>
                  </div>
                </Panel>
              </div>
            ))}
          </div>

          <Panel className="mb-md">
            <div className="flex items-center justify-between mb-md">
              <Title>{lang.guestsByArrivalTime}</Title>
              <GuestTypeFilter
                options={guestTypeOptions}
                value={guestTypes}
                onChange={(value) => setGuestTypes(value || [])}
              />
            </div>
            <ArrivalTimeChart slots={chartSlots} guestView={guestView} />
          </Panel>

          <Panel>
            <Title className="mb-md">{lang.arrivalTimeSummary}</Title>
            <Table
              columns={columns}
              data={data}
              loading={loadingArrivalTimeStats}
              loader={5}
              sort={sort}
              setSort={setSort}
              minWidth="600px"
            />
          </Panel>
        </>
      ) : (
        <Container className="bg-white text-center min-h-page flex items-center border rounded">
          <div className="m-auto p-lg">
            <Image className="mx-auto my-lg" src={NoData} />
            <Text color="text-gray-300" className="mt-md mb-md md:text-md">
              {lang.noData}
            </Text>
            <Text color="text-gray" size="text-sm" className="mt-xs">
              {lang.noArrivalTimeData}
            </Text>
          </div>
        </Container>
      )}
    </ModuleWrapper>
  );
};

export default ArrivalTimeReport;
